/**
 * Voice Mode Socket.IO Service
 * Handles real-time bidirectional audio streaming (PCM16 format, 24kHz)
 * This is an unpublished work containing 3M confidential and proprietary information.
 * Disclosure, use or reproduction without the written authorization of 3M is prohibited.
 * If publication occurs, the following notice applies:
 * Copyright (C) 2025, 3M. All rights reserved.
 */

import { io, Socket } from 'socket.io-client';
import { IPublicClientApplication } from '@azure/msal-browser';
import { AudioCaptureManager } from './audioCaptureManager';
import logger from './logger';

interface QueuedAudioChunk {
  data: string;
  isLastChunk: boolean;
  sequence?: number | null;
  turnId?: string | number | null;
}

export type VoiceModeStatus = 'disconnected' | 'connecting' | 'ready' | 'error';

export interface VoiceModeCallbacks {
  deviceId?: string; // Optional microphone device ID
  onStatusChange?: (status: VoiceModeStatus) => void;
  onConnected?: () => void; // Socket connected successfully (before assistant ready)
  onAssistantReady?: () => void;
  onAudioLevel?: (level: number) => void;
  onFrequencyData?: (data: Uint8Array) => void;
  onError?: (error: Error) => void;
  onNoAudioDetected?: () => void;
  onAudioPlayback?: (audioHex: string) => void; // Received audio chunk from assistant
  onTextDelta?: (text: string) => void; // Received text transcript chunk
  onFunctionCall?: (message: string) => void; // Function call status message
  onSearchSources?: (data: { query: string; sources: Array<{ url: string; domain: string; title: string }> }) => void; // Web search sources from AWS Bedrock Nova
  onSearchProgress?: (data: { message: string; query: string; elapsed: number }) => void; // Search progress updates
  onTranscriptionComplete?: (transcript: string, audioData?: string[], messageId?: string) => void; // User's speech fully transcribed with audio chunks array and message ID
  onUserTranscriptDelta?: (text: string) => void; // Partial transcription of user's speech
  onAssistantResponseComplete?: (audioData?: string[]) => void; // Assistant finished speaking with optional audio chunks
  onUserSpeechStart?: () => void; // User speech detected
  onUserSpeechEnd?: () => void; // User stopped speaking
  onAssistantResponseStart?: () => void; // Assistant started speaking
  onUserTranscriptStart?: () => void; // User transcript turn started
  onInactivityWarning?: () => void; // Inactivity warning triggered (30 seconds of no speech)
  onInactivityCountdown?: (secondsRemaining: number) => void; // Countdown update (1-60 seconds)
  onInactivityTimeout?: () => void; // Inactivity timeout - voice mode should end
  onPauseStateChange?: (isPaused: boolean) => void; // Pause state changed (user or inactivity)
}

class VoiceModeService {
  private socket: Socket | null = null;
  private callbacks: VoiceModeCallbacks = {};
  private audioCapture: AudioCaptureManager | null = null;
  private msalInstance: IPublicClientApplication | null = null;
  private audioContext: AudioContext | null = null;
  private audioQueue: QueuedAudioChunk[] = [];
  private activeSourceNodes: AudioBufferSourceNode[] = [];
  private isPlayingAudio: boolean = false;
  private currentTranscript: string = ''; // Accumulate assistant's text response
  private currentUserTranscript: string = '';
  private nextPlaybackTime: number | null = null;
  private pendingAssistantCompletion: boolean = false;
  private pendingUserTranscriptCompletion: boolean = false;
  private isUserSpeaking: boolean = false;
  private currentAssistantTurnId: string | number | null = null;
  private currentUserTurnId: string | number | null = null;
  private hasGeneratedAssistantTurnId: boolean = false;
  private hasGeneratedUserTurnId: boolean = false;
  private hasFunctionCallInProgress: boolean = false; // Track if function call is in progress (waiting for post-call response)
  private hasFunctionCallBeenReceived: boolean = false; // Track if ANY function_call_message was received this assistant turn (survives even after function completes)
  private audioCompleteReceived: boolean = false; // Track if audio_complete has been received from server for current turn
  private heartbeatInterval: NodeJS.Timeout | null = null; // Interval for sending keepalive signals
  private isAssistantActive: boolean = false; // Track if assistant is currently sending messages or processing function calls
  private isDisconnecting: boolean = false; // Track if disconnect is in progress to prevent race conditions
  private awaitingFinalization: boolean = false; // Track if message is waiting for potential function call before finalizing
  private finalizationGraceTimer: NodeJS.Timeout | null = null; // Grace period timer — holds finalization open while we wait for a potential function_call_message
  private isPaused: boolean = false; // Track if audio capture is paused
  private hasEverDetectedAudio: boolean = false; // Track if we've ever detected audio in this session (prevents repeated "no audio" dialogs)
  
  // INACTIVITY TRACKING: Monitor user activity to auto-end voice mode after inactivity
  private lastActivityTimestamp: number = Date.now(); // Last time user spoke or backend registered speech
  private inactivityWarningTimer: NodeJS.Timeout | null = null; // Timer for 30-second warning
  private inactivityCountdownTimer: NodeJS.Timeout | null = null; // Timer for 60-second countdown
  private inactivityCountdownInterval: NodeJS.Timeout | null = null; // Interval for updating countdown
  private isInactivityWarningActive: boolean = false; // Track if warning is currently active
  
  // EVENT TRACKING: Track all text_delta events by role to confirm backend behavior
  private eventTracking = {
    textDeltaUserCount: 0,
    textDeltaAssistantCount: 0,
    speechStartedCount: 0,
    speechStoppedCount: 0,
    transcriptionCompleteCount: 0,
    userTranscriptCount: 0,
    userTextDeltaExamples: [] as any[]
  };

  /**
   * Prime the TLS session with the APIM host by issuing a lightweight HTTPS
   * request before opening the wss:// Socket.IO connection.
   *
   * When APIM has "Negotiate Client Certificate" enabled, the server sends a
   * TLS CertificateRequest during the handshake.  Browsers handle this
   * transparently for ordinary fetch/XHR requests (showing the OS certificate
   * picker when needed), but some browsers do NOT surface that prompt for a
   * WebSocket upgrade.  By making a regular HTTPS fetch to the same host
   * first, the browser completes the TLS handshake (and any certificate
   * selection) over HTTP.  The subsequent wss:// connection reuses that
   * already-negotiated TLS session, bypassing the issue entirely.
   *
   * The request is fire-and-forget: we intentionally swallow all errors so
   * that a non-200 response or network hiccup never prevents voice mode from
   * attempting to connect.
   *
   * @param host - The HTTPS origin to prime (e.g. "https://navigator.ai.3m.com")
   * @param accessToken - Bearer token to attach so APIM does not reject the probe with 401
   */
  private async primeTlsSession(host: string, accessToken: string): Promise<void> {
    try {
      // Hit the host root rather than /voice-live (which is WebSocket-only and
      // returns 404 for plain HTTP).  Any response code is acceptable here —
      // all we need is for the browser to complete the TLS handshake so the
      // subsequent wss:// upgrade can reuse the negotiated session.
      const probeUrl = `${host}/`;
      logger.log('[VoiceModeService] Priming TLS session for client-certificate negotiation:', probeUrl);
      // A HEAD request is the lightest option – no response body is transferred.
      // We include the Authorization header so APIM policy does not short-circuit
      // before the TLS handshake completes.
      const probeResponse = await fetch(probeUrl, {
        method: 'HEAD',
        headers: { Authorization: `Bearer ${accessToken}` },
        // Avoid following redirects that might land on a different host
        redirect: 'manual',
        // Keep the probe within a reasonable timeout
        signal: AbortSignal.timeout(5000),
      });
      logger.log(`[VoiceModeService] TLS session primed — HTTP ${probeResponse.status} (any status is acceptable, TLS handshake completed)`);
    } catch (err) {
      // Non-fatal: log and continue.  The wss:// connection will still be
      // attempted; this only means the TLS pre-prime did not succeed.
      logger.warn('[VoiceModeService] TLS priming request did not succeed (non-fatal):', err);
    }
  }

  /**
   * Get the access token for API requests
   */
  private async getAccessToken(): Promise<string> {
    if (!this.msalInstance) {
      throw new Error('MSAL instance not set. Call setMsalInstance() first.');
    }

    const accounts = this.msalInstance.getAllAccounts();
    
    if (accounts.length === 0) {
      throw new Error('No authenticated account found');
    }

    const request = {
      scopes: [`${process.env.REACT_APP_CLIENT_ID || '262b88ff-8a8d-49fc-8485-017906bd3c29'}/user_impersonation`],
      account: accounts[0],
    };

    try {
      const response = await this.msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (silentError: any) {
      // Check if it's an interaction_in_progress error
      if (silentError?.errorCode === 'interaction_in_progress') {
        logger.error('[VoiceModeService] Cannot acquire token: interaction already in progress');
        throw new Error('Authentication interaction already in progress. Please wait and try again.');
      }
      
      // For other errors, try popup
      try {
        const response = await this.msalInstance.acquireTokenPopup(request);
        return response.accessToken;
      } catch (popupError: any) {
        // If popup also fails with interaction_in_progress, surface a better error
        if (popupError?.errorCode === 'interaction_in_progress') {
          throw new Error('Authentication interaction already in progress. Please wait and try again.');
        }
        throw popupError;
      }
    }
  }

  /**
   * Set the MSAL instance for authentication
   * @param msalInstance - The MSAL instance from the application
   */
  setMsalInstance(msalInstance: IPublicClientApplication): void {
    this.msalInstance = msalInstance;
  }

  /**
   * Update audio visualization callbacks (for when component remounts)
   * @param callbacks - New callbacks for audio level and frequency data
   */
  updateAudioCallbacks(callbacks: Pick<VoiceModeCallbacks, 'onAudioLevel' | 'onFrequencyData' | 'onAssistantReady'>): void {
    if (callbacks.onAudioLevel) {
      this.callbacks.onAudioLevel = callbacks.onAudioLevel;
    }
    if (callbacks.onFrequencyData) {
      this.callbacks.onFrequencyData = callbacks.onFrequencyData;
    }
    if (callbacks.onAssistantReady) {
      this.callbacks.onAssistantReady = callbacks.onAssistantReady;
    }
    logger.log('[VoiceModeService] Audio callbacks updated');
  }

  /**
   * Connect to the voice mode server via Socket.IO, called by in ChatInput.tsx when voiceMode is activated
   * The flow is: get access token -> connect socket -> start session -> start audio capture
   * @param serverUrl - The WebSocket server URL (e.g., wss://ai-np-apim-main.mmm.com)
   * @param callbacks - Callbacks for connection events
   */
  async connect(serverUrl: string = 'ws://localhost:8000', callbacks: VoiceModeCallbacks = {}): Promise<void> {
    if (this.socket?.connected) {
      return;
    }

    // If a stale socket exists (e.g. previous timeout/error) but isn't connected,
    // destroy it fully before creating a new one. Otherwise two Socket.IO managers
    // compete for the same connection and the new one always times out.
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.callbacks = callbacks;
    this.callbacks.onStatusChange?.('connecting');

    this.isDisconnecting = false; // Reset disconnecting flag for new connection
    this.isPaused = false; // Reset paused state for new connection
    
    // Clear any lingering inactivity timers from previous session
    this.clearInactivityTimers();
    logger.log('[VoiceModeService] 🧹 Cleared any lingering timers before new connection');
    
    this.currentAssistantTurnId = null;
    this.currentUserTurnId = null;
    this.pendingAssistantCompletion = false;
    this.pendingUserTranscriptCompletion = false;
    this.currentTranscript = '';
    this.currentUserTranscript = '';
    this.isUserSpeaking = false;
    this.hasFunctionCallInProgress = false; // Reset function call flag
    this.hasFunctionCallBeenReceived = false; // Reset function call received flag
    this.audioCompleteReceived = false; // Reset audio complete flag
    this.activeSourceNodes = []; // Clear any stale source nodes so inactivity timer can start
    this.awaitingFinalization = false;
    if (this.finalizationGraceTimer !== null) {
      clearTimeout(this.finalizationGraceTimer);
      this.finalizationGraceTimer = null;
    }

    logger.log('[VoiceModeService] Connecting to:', serverUrl);

    try {
      // Get access token before connecting
      const accessToken = await this.getAccessToken();

      // Get user email for identification
      const accounts = this.msalInstance?.getAllAccounts();
      const userEmail = accounts?.[0]?.username || 'unknown@user.com';

      logger.log('[VoiceModeService] Attempting connection with config:', {
        serverUrl,
        path: '/voice-live',
        userEmail,
        transport: 'websocket'
      });
      const apimBaseUrl = process.env.REACT_APP_APIM_BASE_URL || 'https://navigator.ai.3m.com';

      // Prime the TLS session so the browser completes any client-certificate
      // negotiation (required when APIM "Negotiate Client Certificate" is ON)
      // over a regular HTTPS request before the wss:// upgrade.
      await this.primeTlsSession(apimBaseUrl, accessToken);

      // Socket.IO connection matching production documentation
      // The path should be where Socket.IO server is listening
      const socketConfig = {
        path: '/voice-live',
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        auth: {
          token: accessToken
        },
        query: {
          upin: userEmail  // Use full email as UPIN (matches working wscat command)
        }
      };
      // socket io configuration, TODO: change to env variable
      this.socket = io(apimBaseUrl, socketConfig);
      
      // Access internal Socket.IO engine for more debugging
      this.socket.io.on('open', () => { logger.log('Socket.io engine opened')});
      
      this.socket.io.on('error', (error: any) => {
        logger.error('[VoiceModeService] Socket.IO Engine error:', error);
      });
      
      this.socket.io.on('close', (reason: any) => {
        logger.log('[VoiceModeService] Socket.IO Engine closed:', reason);
      });
    } catch (error) {
      logger.error('[VoiceModeService] Failed to get access token:', error);
      this.callbacks.onStatusChange?.('error');
      this.callbacks.onError?.(error as Error);
      return;
    }

    // Listen for connection
    this.socket.on('connect', () => {
      // Start voice session
      logger.log('[VoiceModeService] Connected and starting voice session');
      this.socket?.emit('start_session', {});
      
      // Notify that we've connected (before assistant is ready)
      this.callbacks.onConnected?.();
    });

    // Connection confirmation
    this.socket.on('connection_established', (data: any) => {
      logger.log('[VoiceModeService] Connection confirmed:', data);
      if (data.sid) {
        logger.log('[VoiceModeService] Session ID:', data.sid);
      }
    });

    // Wait for assistant to be ready
    this.socket.on('assistant_ready', async () => {
      // Check if we're disconnecting before starting audio capture
      if (this.isDisconnecting) {
        logger.log('[VoiceModeService] Disconnecting in progress - skipping audio capture start');
        return;
      }
      
      // Clear any lingering timers from previous session (safety measure)
      this.clearInactivityTimers();
      logger.log('[VoiceModeService] 🧹 Cleared timers at session start (safety)');
      
      this.callbacks.onStatusChange?.('ready');
      
      // Add small delay to ensure backend is fully ready to receive audio
      // This prevents race condition where assistant_ready is sent before backend is fully initialized
      logger.log('[VoiceModeService] Waiting 200ms for backend to fully initialize...');
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Check again if disconnecting after delay
      if (this.isDisconnecting) {
        logger.log('[VoiceModeService] Disconnecting in progress after delay - skipping audio capture start');
        return;
      }
      
      // Start audio capture when assistant is ready
      try {
        logger.log('[VoiceModeService] 🎤 Assistant is ready, now starting audio capture...');
        logger.log('[VoiceModeService] 🔍 Current state - socket connected:', !!this.socket?.connected, 'isPaused:', this.isPaused, 'isDisconnecting:', this.isDisconnecting);
        
        // CRITICAL: Stop any existing audio capture before creating a new one
        if (this.audioCapture) {
          logger.log('[VoiceModeService] ⚠️ Found existing audio capture - stopping it first');
          try {
            await this.audioCapture.stop();
          } catch (err) {
            logger.error('[VoiceModeService] Error stopping existing audio capture:', err);
          }
          this.audioCapture = null;
        }
        
        const audioCaptureManager = new AudioCaptureManager();
        this.audioCapture = audioCaptureManager;
        this.isUserSpeaking = false;
        this.currentUserTranscript = '';
        this.pendingUserTranscriptCompletion = false;
        
        await audioCaptureManager.start({
          deviceId: this.callbacks.deviceId,
          onAudioData: (base64Audio) => {
            // Send audio data to server using 'audio_stream' event (matches production API)
            if (this.socket?.connected && !this.isPaused) {
              this.socket.emit('audio_stream', { audio: base64Audio });
              
              // Log occasionally to confirm we're sending audio
              if (Math.random() < 0.01) { // Log ~1% of chunks
                logger.log('[VoiceModeService] ✅ Sending audio chunk to server');
              }
            } else if (this.isPaused && Math.random() < 0.01) {
              // Only log occasionally when paused to avoid spam
              logger.log('[VoiceModeService] ⏸️ Audio capture paused, not sending audio');
            } else if (!this.socket?.connected) {
              logger.warn('[VoiceModeService] ⚠️ Socket not connected, cannot send audio');
            }
          },
          onAudioLevel: (level) => {
            // Mark audio as detected as soon as the mic picks up any non-trivial sound.
            // This mirrors AudioCaptureManager's own threshold (0.01) so we never show
            // the "no microphone detected" dialog if the user has spoken at any point.
            if (!this.hasEverDetectedAudio && level > 0.05) {
              this.hasEverDetectedAudio = true;
              logger.log('[VoiceModeService] 🎙️ Audio level detected on frontend (level:', level.toFixed(3), ') - suppressing no-audio warning');
            }
            // Pass audio level to callback for visualization
            this.callbacks.onAudioLevel?.(level);
          },
          onFrequencyData: (data) => {
            // Pass frequency data to callback for visualization
            this.callbacks.onFrequencyData?.(data);
          },
          onError: (error) => {
            logger.error('[VoiceModeService] Audio capture error:', error);
            this.callbacks.onStatusChange?.('error');
            this.callbacks.onError?.(error);
          },
          onNoAudioDetected: () => {
            logger.log('[VoiceModeService] 🔇 onNoAudioDetected fired. hasEverDetectedAudio:', this.hasEverDetectedAudio, '| onNoAudioDetected callback set:', !!this.callbacks.onNoAudioDetected);
            // Only show the dialog if we've never detected audio in this session
            // This prevents it from showing during inactivity periods
            if (!this.hasEverDetectedAudio) {
              logger.log('[VoiceModeService] ❌ No audio detected - calling this.callbacks.onNoAudioDetected. Callback exists:', !!this.callbacks.onNoAudioDetected);
              this.callbacks.onNoAudioDetected?.();
              logger.log('[VoiceModeService] ❌ this.callbacks.onNoAudioDetected call completed');
            } else {
              logger.log('[VoiceModeService] ✅ No audio timer fired, but audio was detected before - ignoring');
            }
          }
        });
        
        // Check if disconnect was called while we were starting audio capture
        if (this.isDisconnecting) {
          logger.log('[VoiceModeService] Disconnect was called during audio capture start - stopping now');
          await audioCaptureManager.stop();
          this.audioCapture = null;
          return;
        }
      } catch (error) {
        logger.error('[VoiceModeService] Failed to start audio capture:', error);
        this.callbacks.onStatusChange?.('error');
        this.callbacks.onError?.(error as Error);
      }
      
      this.callbacks.onAssistantReady?.();
      
      // Start inactivity monitoring after assistant is ready
      this.startInactivityMonitoring();
    });

    // Handle server errors
    this.socket.on('error', async (data: any) => {
      logger.error('[VoiceModeService] Server error:', data.message || data);
      const errorMessage = data.message || 'Unknown server error';
      
      // Clear inactivity timers immediately on error
      this.clearInactivityTimers();
      
      // If function call was in progress, finalize the assistant message before handling error
      if (this.hasFunctionCallInProgress) {
        this.hasFunctionCallInProgress = false;
        this.handleAssistantTurnComplete();
      }
      
      // Stop heartbeat
      this.stopHeartbeat();
      
      // Stop audio capture to prevent microphone from continuing to run
      if (this.audioCapture) {
        await this.audioCapture.stop();
        this.audioCapture = null;
      }
      
      // Stop audio playback
      this.stopAssistantAudio();
      
      // Notify about error status
      this.callbacks.onStatusChange?.('error');
      
      // Check for authentication errors
      if (errorMessage.includes('Authentication failed') || errorMessage.includes('Unauthorized')) {
        this.callbacks.onError?.(new Error('Authentication failed. Please sign in again.'));
      } else {
        this.callbacks.onError?.(new Error(errorMessage));
      }
    });

    // Handle audio playback from assistant
    this.socket.on('audio_playback', (data: any) => {
      const audioChunk: string | undefined = data?.audio;
      const chunkMeta = {
        length: typeof audioChunk === 'string' ? audioChunk.length : 0,
        sequence: data?.sequence ?? data?.chunk_index ?? data?.chunkIndex ?? null,
        turnId: data?.turn_id ?? data?.turnId ?? null,
        isLastChunk: data?.is_last_chunk ?? data?.isLastChunk ?? data?.last ?? false,
      };

      if (typeof audioChunk === 'string' && audioChunk.length > 0) {
        if (this.currentAssistantTurnId === null) {
          this.handleAssistantTurnStart();
        }

        this.audioQueue.push({
          data: audioChunk,
          isLastChunk: Boolean(chunkMeta.isLastChunk),
          sequence: chunkMeta.sequence,
          turnId: chunkMeta.turnId,
        });
        
        this.callbacks.onAudioPlayback?.(audioChunk);
        this.flushAudioQueue();
      }
    });

    // Handle text deltas (assistant's transcript) so we can display it in the chat
    this.socket.on('text_delta', (data: any) => {
      const text = data?.text ?? data?.delta ?? '';
      const role = this.getDeltaRole(data);
      const isFinal = this.isFinalDelta(data);
      
      // Track event counts
      if (role === 'user') {
        this.eventTracking.textDeltaUserCount++;
        // Store first 3 examples
        if (this.eventTracking.userTextDeltaExamples.length < 3) {
          this.eventTracking.userTextDeltaExamples.push({ text, isFinal, data });
        }
        console.warn('⚠️ [USER_TEXT_DELTA_DETECTED] Backend sent text_delta with role=user! Count:', this.eventTracking.textDeltaUserCount, {
          text,
          isFinal,
          rawData: data
        });
      } else {
        this.eventTracking.textDeltaAssistantCount++;
      }

      if (role === 'user') {
        // Start a new user turn if one isn't active
        if (this.currentUserTurnId === null) {
          this.handleUserTurnStart();
        }

        if (typeof text === 'string' && text.length > 0) {
          this.currentUserTranscript += text;
          this.pendingUserTranscriptCompletion = true;
          this.callbacks.onUserTranscriptDelta?.(text);
        }

        if (isFinal) {
          this.handleUserTurnComplete();
        }
      } else {
        // Start a new assistant turn if one isn't active
        if (this.currentAssistantTurnId === null) {
          this.handleAssistantTurnStart();
        } else if (this.hasFunctionCallInProgress) {
          // Function call completed, now receiving post-function-call response
          this.hasFunctionCallInProgress = false;
          
          // Note: Don't start timer here - audio chunks for the post-function response
          // might be arriving shortly. Let the source.onended callback handle it
          // when the last audio chunk actually finishes playing.
        } else {
          logger.log('[VoiceModeService] Continuing existing assistant message');
        }

        // User message already finalized in speech_stopped handler
        // No need to finalize again here

        if (typeof text === 'string' && text.length > 0) {
          this.currentTranscript += text;
          this.callbacks.onTextDelta?.(text);

          // Debounce finalization: if the grace period timer is active, reset it.
          // Text may still be streaming after the audio buffer empties (e.g. the
          // post-function-call response arrives as text first, audio second).
          // We must not finalize until both text AND audio have gone quiet.
          if (this.awaitingFinalization && this.finalizationGraceTimer !== null) {
            clearTimeout(this.finalizationGraceTimer);
            this.finalizationGraceTimer = null;
            this.awaitingFinalization = false;
            logger.log('[VoiceModeService] text_delta reset finalization grace timer — text still streaming');
            this.scheduleFinalization();
          }
        }

        // Don't finalize on isFinal - wait for audio_complete event instead
      }
    });

    // Handle function call messages (ex. Searching the web, calling plugins like weather)
    this.socket.on('function_call_message', (data: any) => {
      logger.log('[VoiceModeService] Received function_call_message:', data.message, data);
      if (data.message) {
        // If no assistant message exists yet, create one now
        // This handles cases where function_call_message arrives before text_delta
        // If no assistant message exists yet, create one now
        // This handles cases where function_call_message arrives before text_delta
        if (this.currentAssistantTurnId === null) {
          logger.log('[VoiceModeService] Function call arrived before text_delta - creating assistant message now');
          this.handleAssistantTurnStart();
        } else {
          logger.log('[VoiceModeService] Function call continuing existing assistant message');
        }
        
        // Mark that we have a function call in progress
        // This prevents message finalization until we get the post-function-call response
        this.hasFunctionCallInProgress = true;
        this.hasFunctionCallBeenReceived = true; // Permanent per-turn flag: a function call was received

        // Cancel any pending finalization grace timer — the function call arrived in time
        if (this.finalizationGraceTimer !== null) {
          clearTimeout(this.finalizationGraceTimer);
          this.finalizationGraceTimer = null;
          this.awaitingFinalization = false;
          logger.log('[VoiceModeService] function_call_message cancelled pending finalization grace timer — keeping turn open');
        }
        
        // Start/continue heartbeat during function call
        this.startHeartbeat();
        
        this.callbacks.onFunctionCall?.(data.message);
      }
    });

    // Handle backend speech detection events
    this.socket.on('speech_started', (data: any) => {
      this.eventTracking.speechStartedCount++;
      
      logger.log('[VoiceModeService] 🎤 speech_started event:', data);
      
      // Clear inactivity timer - user is speaking
      // DO NOT restart timer here - it will be restarted when all activity completes
      this.clearInactivityTimers();
      
      // Mark that we've detected audio in this session
      this.hasEverDetectedAudio = true;
      
      // User is interrupting - stop any assistant audio playback
      if (this.isPlayingAudio) {
        this.stopAssistantAudio();
      }
      
      // If user interrupts during function call, finalize the assistant message and clear function call flag
      if (this.hasFunctionCallInProgress) {
        this.hasFunctionCallInProgress = false;
        this.handleAssistantTurnComplete();
      }
      
      // Backend confirmed speech - create the "Transcribing..." UI message
      this.handleUserTurnStart();
    });

    this.socket.on('speech_stopped', (data: any) => {
      this.eventTracking.speechStoppedCount++;
      
      logger.log('[VoiceModeService] 🛑 speech_stopped event:', data);
    });

    this.socket.on('audio_complete', (data: any) => {
      // Mark that the server has signalled audio is done for this turn.
      // We do NOT finalize the assistant message here because function_call_message
      // events can arrive AFTER audio_complete (e.g. when web search is slow or fails).
      // Instead we set a flag and let source.onended — which fires after audio has
      // actually finished playing — perform the finalization.  By that point all
      // in-flight function_call_message events will have been processed.
      this.audioCompleteReceived = true;

      // If a function call is already known to be in progress, keep the heartbeat alive.
      if (this.hasFunctionCallInProgress) {
        if (!this.heartbeatInterval) {
          this.startHeartbeat();
        }
      }

      // Edge case: all audio finished playing BEFORE audio_complete arrived.
      // source.onended already ran but couldn't finalize (audioCompleteReceived was false).
      // Now that the flag is set, enter the finalization grace period if there are no more active sources.
      if (this.activeSourceNodes.length === 0 && !this.isPlayingAudio) {
        if (!this.hasFunctionCallInProgress && !this.awaitingFinalization) {
          this.scheduleFinalization();
        }
      }
      // Otherwise finalization will happen in source.onended once all audio has played out.
    });

    // Handle explicit transcription completion events if provided by server
    this.socket.on('transcription_complete', (data: any = {}) => {
      this.eventTracking.transcriptionCompleteCount++;
      const transcript = data?.transcript ?? data?.text ?? '';
      
      // CRITICAL LOGGING: Track transcription_complete events
      logger.log('[TRANSCRIPTION_COMPLETE_TRACKING] transcription_complete event received. Count:', this.eventTracking.transcriptionCompleteCount, {
        hasTranscriptInEvent: Boolean(transcript),
        transcriptLength: typeof transcript === 'string' ? transcript.length : 0,
        transcriptPreview: typeof transcript === 'string' ? transcript.substring(0, 100) : '',
        accumulatedLength: this.currentUserTranscript.length,
        accumulatedPreview: this.currentUserTranscript.substring(0, 100),
        rawData: data
      });
      
      // If the event includes the full transcript, use it; otherwise use accumulated
      if (typeof transcript === 'string' && transcript.length > 0) {
        this.currentUserTranscript = transcript;
      }
      
      this.handleUserTurnComplete();
    });

    // Handle sources from function calls (e.g., web search results)
    this.socket.on('function_sources', (data: any) => {
      const sources = data?.sources || data?.results || [];

      if (sources.length > 0) {
        this.callbacks.onFunctionCall?.(JSON.stringify({ sources }));
      } else {
        console.warn('⚠️ function_sources event had NO sources!');
      }
    });
    
    // Alternative event name for search results
    this.socket.on('search_results', (data: any) => {
      const sources = data?.sources || data?.results || [];
      if (sources.length > 0) {
        this.callbacks.onFunctionCall?.(JSON.stringify({ sources }));
      } else {
        console.warn('⚠️ search_results event had NO sources!');
      }
    });
    
    // Handle search_sources event from AWS Bedrock Nova grounding
    this.socket.on('search_sources', (data: any) => {
      const sources = data?.sources || [];
      const query = data?.query || '';
      
      // Ensure we have an assistant message for the sources
      if (this.currentAssistantTurnId === null) {
        this.handleAssistantTurnStart();
      }
      
      // Call the dedicated sources callback if available
      if (this.callbacks.onSearchSources) {
        this.callbacks.onSearchSources({ query, sources });
      } else {
        // Fallback to existing onFunctionCall for backwards compatibility
        if (sources.length > 0) {
          this.callbacks.onFunctionCall?.(JSON.stringify({ sources }));
        }
      }
    });
    
    // Handle search_progress event for showing search status updates
    this.socket.on('search_progress', (data: any) => {
      const message = data?.message || '';
      const query = data?.query || '';
      const elapsed = data?.elapsed || 0;
      
      logger.log('Progress message:', message);
      logger.log('Query:', query);
      logger.log('Elapsed time:', elapsed, 'seconds');
      
      // Ensure we have an assistant message for the progress updates
      if (this.currentAssistantTurnId === null) {
        this.handleAssistantTurnStart();
      }
      
      // Call the dedicated progress callback if available
      if (this.callbacks.onSearchProgress) {
        this.callbacks.onSearchProgress({ message, query, elapsed });
      } else {
        // Fallback to existing onFunctionCall for backwards compatibility
        this.callbacks.onFunctionCall?.(message);
      }
    });

    // Handle user transcript event (new backend feature)
    this.socket.on('user_transcript', (data: any = {}) => {
      this.eventTracking.userTranscriptCount++;
      const transcript = data?.transcript ?? data?.text ?? '';
      
      logger.log('[VoiceModeService] 📝 user_transcript event:', {
        hasTranscript: Boolean(transcript),
        transcriptLength: typeof transcript === 'string' ? transcript.length : 0,
        transcriptPreview: typeof transcript === 'string' ? transcript.substring(0, 100) : ''
      });
      
      // Store the transcript
      if (typeof transcript === 'string' && transcript.length > 0) {
        this.currentUserTranscript = transcript;
      }
      
      // Finalize the user message with the transcript
      if (this.pendingUserTranscriptCompletion) {        
        // Finalize with transcript (backend handles all audio)
        if (this.currentUserTranscript) {
          this.callbacks.onTranscriptionComplete?.(this.currentUserTranscript, undefined, undefined);
        }
        
        this.currentUserTranscript = '';
        this.pendingUserTranscriptCompletion = false;
      }
    });

    // Handle server log messages, will print session initialization logs (ex. start_session handler called)
    this.socket.on('log', (data: any) => {
      logger.log('[VoiceModeService] Server log:', data.message);
    });

    // Handle general messages from server
    this.socket.on('message', (data: any) => {
      logger.log('[VoiceModeService] Server message:', data.data || data);
    });

    // Handle connection errors
    // Track connection attempts to only show error on final failure
    let connectionAttempts = 0;
    const maxAttempts = 5; // Should match reconnectionAttempts in socket config
    
    this.socket.on('connect_error', (error: any) => {
      connectionAttempts++;
      // Enhanced error details
      logger.error('[VoiceModeService] Error details:', {
        message: error.message,
        type: error.type,
        description: error.description,
        context: error.context,
        stack: error.stack
      });
      
      // Log the complete error object structure
      logger.error('[VoiceModeService] Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
      
      // If the description is an Event object, extract more details
      if (error.description && error.description instanceof Event) {
        const wsEvent = error.description as any;
        logger.error('[VoiceModeService] WebSocket Event details:', {
          type: wsEvent.type,
          target: wsEvent.target ? {
            url: wsEvent.target.url,
            readyState: wsEvent.target.readyState,
            protocol: wsEvent.target.protocol,
            extensions: wsEvent.target.extensions,
            bufferedAmount: wsEvent.target.bufferedAmount
          } : 'no target',
          isTrusted: wsEvent.isTrusted,
          eventPhase: wsEvent.eventPhase,
          bubbles: wsEvent.bubbles,
          cancelable: wsEvent.cancelable,
          defaultPrevented: wsEvent.defaultPrevented,
          composed: wsEvent.composed,
          timeStamp: wsEvent.timeStamp
        });
        
        // Check for close event details if available
        if (wsEvent.code !== undefined) {
          logger.error('[VoiceModeService] WebSocket close code, reason, was clean:', wsEvent.code, wsEvent.reason, wsEvent.wasClean);
        }
      }
      
      // Provide troubleshooting hints based on error type
      if (error.type === 'TransportError') {
        logger.error('[VoiceModeService] 🔍 Troubleshooting TransportError:');
        logger.error('  1. Check if the server is running and accessible');
        logger.error('  2. Verify the server URL and path are correct');
        logger.error('  3. Check if firewall/proxy is blocking WebSocket connections');
        logger.error('  4. Try using network inspection tools (F12 > Network > WS tab)');
        logger.error('  5. Check server logs for rejection/authentication issues');
        logger.error('  6. Verify CORS settings if applicable');
        logger.error(`  7. Test direct connection: wscat -c "${serverUrl}/voice-live/?upin=${this.msalInstance?.getAllAccounts()?.[0]?.username || 'unknown'}&EIO=4&transport=websocket"`);
      }
      
      // Only notify user on the final failed attempt
      if (connectionAttempts >= maxAttempts) {
        logger.error('[VoiceModeService] All connection attempts failed - notifying user');
        
        // Update UI status
        this.callbacks.onStatusChange?.('error');
        
        // Call onError callback with enhanced error message
        const enhancedError = new Error(
          `Failed to connect to voice server after ${maxAttempts} attempts. ` +
          `Error: ${error.message || 'Unknown error'}. ` +
          `Check browser console for detailed debugging information.`
        );
        this.callbacks.onError?.(enhancedError);
      }
    });

    // Handle disconnection
    this.socket.on('disconnect', async (reason) => {
      logger.log('[VoiceModeService] Disconnected:', reason);
      
      // Clear inactivity timers immediately
      this.clearInactivityTimers();
      
      // Stop heartbeat
      this.stopHeartbeat();
      
      // Stop audio capture to prevent microphone from continuing to run
      if (this.audioCapture) {
        await this.audioCapture.stop();
        this.audioCapture = null;
      }
      
      // Stop audio playback
      this.stopAssistantAudio();
      
      this.callbacks.onStatusChange?.('disconnected');
      // Reset attempt counter on disconnect
      connectionAttempts = 0;
    });

    // Handle reconnection attempts
    this.socket.on('reconnect_attempt', () => {
      this.callbacks.onStatusChange?.('connecting');
    });

    // Handle successful reconnection
    this.socket.on('reconnect', (attempt) => {
      connectionAttempts = 0; // Reset counter on successful reconnect
    });

    // Handle reconnection failure (all attempts exhausted)
    this.socket.on('reconnect_failed', () => {
      logger.error('[VoiceModeService] All reconnection attempts failed');
      this.callbacks.onStatusChange?.('error');
      this.callbacks.onError?.(new Error('Failed to reconnect to voice server'));
    });
    
    // Optional: Listen for keepalive acknowledgment (if server implements it)
    // This helps detect if server is still responsive
    this.socket.on('keepalive_ack', () => {
      logger.log('[VoiceModeService] 💓 Keepalive acknowledged by server');
    });
  } // End of connect method

  /**
   * Stop assistant audio playback (for user interruptions)
   */
  private stopAssistantAudio(): void {    
    // Stop all currently playing audio source nodes
    this.activeSourceNodes.forEach((source) => {
      try {
        source.stop();
        source.disconnect();
      } catch (e) {
        // Source might have already stopped naturally, ignore error
      }
    });
    this.activeSourceNodes = [];
    
    // Clear the audio queue so no more chunks will play
    this.audioQueue = [];
    
    // Reset playback state (AudioContext itself stays active for future playback)
    this.isPlayingAudio = false;
    this.nextPlaybackTime = null;
    
    // Don't complete the assistant turn - the backend will send isFinal when done
    // This prevents splitting up messages that are still streaming

    // Cancel any pending finalization grace timer (user interrupted)
    if (this.finalizationGraceTimer !== null) {
      clearTimeout(this.finalizationGraceTimer);
      this.finalizationGraceTimer = null;
      this.awaitingFinalization = false;
    }
    
    // Note: We don't suspend the AudioContext because we want it ready for the next assistant response
    logger.log('[VoiceModeService] Audio queue cleared and active sources stopped, ready for next playback');
  }

  /**
   * Flush queued audio chunks to the AudioContext for smooth playback
   */
  private flushAudioQueue(): void {
    if (!this.audioQueue.length) {
      return;
    }

    if (!this.audioContext) {
      this.audioContext = new AudioContext({ sampleRate: 24000 });
    }

    while (this.audioQueue.length > 0 && this.audioContext) {
      const chunk = this.audioQueue.shift();
      if (!chunk) {
        continue;
      }

      const audioBuffer = this.createAudioBufferFromChunk(chunk.data);
      if (!audioBuffer) {
        continue;
      }

      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(this.audioContext.destination);

      if (this.nextPlaybackTime === null) {
        this.nextPlaybackTime = this.audioContext.currentTime;
      }

      const startTime = Math.max(this.nextPlaybackTime, this.audioContext.currentTime);
      source.start(startTime);
      this.nextPlaybackTime = startTime + audioBuffer.duration;
      this.isPlayingAudio = true;
      
      // Track this source node so we can stop it if user interrupts
      this.activeSourceNodes.push(source);

      source.onended = () => {
        // Remove from active sources FIRST
        const index = this.activeSourceNodes.indexOf(source);
        if (index > -1) {
          this.activeSourceNodes.splice(index, 1);
        }
        source.disconnect();
        
        const noMoreAudio = this.activeSourceNodes.length === 0;
        
        // Finalize when ALL audio has finished playing AND the server has sent audio_complete.
        // We check noMoreAudio (not chunk.isLastChunk) so that finalization only happens
        // once every queued chunk has actually played out.  By that point any
        // function_call_message events sent after audio_complete will have arrived.
        if (noMoreAudio && this.audioCompleteReceived) {
          if (this.pendingUserTranscriptCompletion) {
            const transcript = this.currentUserTranscript;
            this.currentUserTranscript = '';
            this.pendingUserTranscriptCompletion = false;
            this.callbacks.onTranscriptionComplete?.(transcript);
          }

          this.isPlayingAudio = false;
          this.nextPlaybackTime = null;

          if (this.hasFunctionCallInProgress) {
            // A function call is still running — keep the turn open
            logger.log('[VoiceModeService] Function call in progress after audio finished - keeping turn open');
          } else {
            // No outstanding function call — enter a short grace period before finalizing.
            // A function_call_message could still be in-flight even though the audio buffer
            // is empty (race condition with short pre-search audio responses).
            this.scheduleFinalization();
          }
        } else if (noMoreAudio && !this.audioCompleteReceived) {
          // All queued audio played but server hasn't sent audio_complete yet.
          // This can happen if audio chunks arrive faster than they play; just wait.
          logger.log('[VoiceModeService] All queued audio played but audio_complete not yet received - waiting');
        }
      };
    }
  }

  private createAudioBufferFromChunk(chunk: string): AudioBuffer | null {
    if (!this.audioContext) {
      return null;
    }

    try {
      const arrayBuffer = this.decodeAudioString(chunk);
      if (arrayBuffer.byteLength === 0) {
        return null;
      }

      const int16Array = new Int16Array(arrayBuffer);
      if (int16Array.length === 0) {
        return null;
      }

      const float32Array = new Float32Array(int16Array.length);
      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = Math.min(1, Math.max(-1, int16Array[i] / 32768));
      }

      const sampleRate = this.audioContext.sampleRate || 24000;
      const audioBuffer = this.audioContext.createBuffer(1, float32Array.length, sampleRate);
      audioBuffer.getChannelData(0).set(float32Array);
      return audioBuffer;
    } catch (error) {
      logger.error('[VoiceModeService] Failed to create audio buffer from chunk', error);
      return null;
    }
  }

  private decodeAudioString(audio: string): ArrayBuffer {
    const sanitized = audio.replace(/\s+/g, '');
    const isHex = /^[0-9a-fA-F]+$/.test(sanitized);
    if (isHex) {
      return this.hexToArrayBuffer(sanitized);
    }

    const isBase64 = /^[A-Za-z0-9+/=_-]+$/.test(sanitized);
    if (isBase64) {
      return this.base64ToArrayBuffer(sanitized);
    }

    throw new Error('Unsupported audio encoding format');
  }

  private handleAssistantTurnStart(): void {
    if (this.pendingAssistantCompletion) {
      return; // Already have an active assistant message
    }

    this.currentAssistantTurnId = `assistant-${Date.now()}`;
    this.pendingAssistantCompletion = true;
    this.hasFunctionCallBeenReceived = false; // Reset per-turn flag
    this.audioCompleteReceived = false; // Reset per-turn flag
    this.callbacks.onAssistantResponseStart?.();
    
    // Start heartbeat when assistant becomes active
    this.startHeartbeat();
  }

  /**
   * Schedule turn finalization after a short grace period (default 400 ms).
   *
   * The grace period guards against two race conditions:
   * 1. The audio buffer empties *just before* a `function_call_message` event
   *    arrives from the server (brief pre-search audio response).
   * 2. The audio buffer empties while text_delta events for the post-function
   *    response are still streaming in.  Each arriving text_delta resets the
   *    timer (debounce), so finalization only fires once both audio AND text
   *    have been silent for the full grace window.
   *
   * If a function call is received during the window we cancel the timer and
   * keep the turn open; otherwise we finalize normally.
   */
  private scheduleFinalization(gracePeriodMs: number = 400): void {
    if (this.awaitingFinalization) {
      // Timer already running — nothing to do
      return;
    }

    this.awaitingFinalization = true;
    logger.log(`[VoiceModeService] ⏳ Scheduling finalization grace period (${gracePeriodMs}ms)`);

    this.finalizationGraceTimer = setTimeout(() => {
      this.finalizationGraceTimer = null;
      this.awaitingFinalization = false;

      if (this.hasFunctionCallInProgress) {
        // A function call arrived during the grace period — keep the turn open
        logger.log('[VoiceModeService] Grace period elapsed but function call now in progress — keeping turn open');
        return;
      }

      logger.log('[VoiceModeService] ✅ Grace period elapsed with no function call — finalizing assistant turn');
      this.handleAssistantTurnComplete();
      this.startInactivityTimerIfIdle();
    }, gracePeriodMs);
  }

  private handleAssistantTurnComplete(): void {
    if (this.pendingAssistantCompletion) {
      this.pendingAssistantCompletion = false;
      this.callbacks.onAssistantResponseComplete?.(undefined);
    }
    this.currentAssistantTurnId = null;
    this.hasGeneratedAssistantTurnId = false;
    
    // Stop heartbeat when assistant is done (unless function call is in progress)
    if (!this.hasFunctionCallInProgress) {
      this.stopHeartbeat();
      
      // Start inactivity timer now that assistant is completely done
      // and no function call is in progress
      logger.log('[VoiceModeService] 🕐 Assistant turn complete and no function call - starting inactivity timer');
      this.startInactivityTimerIfIdle();
    }
  }

  private handleUserTurnStart(): void {
    if (this.currentUserTurnId) {
      this.handleUserTurnComplete();
    }

    this.currentUserTurnId = Date.now().toString();
    this.currentUserTranscript = '';
    this.pendingUserTranscriptCompletion = true;
    
    // Notify UI to create "Transcribing..." message
    this.callbacks.onUserTranscriptStart?.();
  }

  private handleUserTurnComplete(): void {
    if (this.pendingUserTranscriptCompletion) {
      const transcript = this.currentUserTranscript;
      this.currentUserTranscript = '';
      this.pendingUserTranscriptCompletion = false;
      
      // Finalize with transcript only (backend handles all audio)
      this.callbacks.onTranscriptionComplete?.(transcript, undefined);
    }
    this.currentUserTurnId = null;
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    try {
      const normalized = base64.replace(/[^A-Za-z0-9+/=]/g, '');
      if (typeof atob === 'function') {
        const binary = atob(normalized);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        return bytes.buffer;
      }
    } catch (error) {
      logger.error('[VoiceModeService] atob decoding failed, attempting Buffer fallback', error);
    }

    const bufferCtor = (globalThis as { Buffer?: { from: (input: string, encoding: string) => { buffer: ArrayBuffer; byteOffset: number; byteLength: number; }; }; }).Buffer;
    if (bufferCtor) {
      const buffer = bufferCtor.from(base64, 'base64');
      return buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength);
    }

    throw new Error('Base64 decoding not supported in this environment');
  }

  private getDeltaRole(delta: any): 'user' | 'assistant' {
    const roleCandidates = [
      delta?.role,
      delta?.speaker,
      delta?.participant,
      delta?.from,
      delta?.metadata?.role,
      delta?.metadata?.speaker,
      delta?.turn?.role,
      delta?.turn?.speaker,
    ];

    for (const candidate of roleCandidates) {
      if (typeof candidate === 'string') {
        const normalized = candidate.toLowerCase();
        if (normalized.includes('user') || normalized.includes('customer')) {
          return 'user';
        }

        if (normalized.includes('assistant') || normalized.includes('agent') || normalized.includes('system')) {
          return 'assistant';
        }
      }
    }

    // Default to assistant since most deltas are assistant responses
    return 'assistant';
  }

  private isFinalDelta(delta: any): boolean {
    const booleanCandidates = [
      delta?.is_final,
      delta?.isFinal,
      delta?.final,
      delta?.done,
      delta?.is_last_segment,
      delta?.isLastSegment,
      delta?.is_last_chunk,
      delta?.isLastChunk,
      delta?.metadata?.is_final,
      delta?.metadata?.isFinal,
      delta?.metadata?.final,
      delta?.metadata?.done,
      delta?.turn?.is_final,
      delta?.turn?.isFinal,
    ];

    if (booleanCandidates.some((value) => value === true)) {
      return true;
    }

    const stringCandidates = [
      delta?.status,
      delta?.state,
      delta?.event,
      delta?.type,
      delta?.metadata?.status,
      delta?.metadata?.state,
    ];

    for (const candidate of stringCandidates) {
      if (typeof candidate === 'string') {
        const normalized = candidate.toLowerCase();
        if (normalized.includes('final') || normalized.includes('complete') || normalized.includes('finish')) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Convert hex string to ArrayBuffer, helper function for audio playback
   */
  private hexToArrayBuffer(hex: string): ArrayBuffer {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes.buffer;
  }

  /**
   * Disconnect from the voice mode server by stopping capture, playback, and clearing queue
   * Called by ChatInput.tsx when voiceMode is deactivated
   * Capture and playback are different - capture is microphone input, playback is assistant audio output
   */
  async disconnect(): Promise<void> {
    logger.log('[VoiceModeService] 🛑 DISCONNECT called - starting cleanup');
    
    // Set flag to prevent race conditions with audio capture initialization
    this.isDisconnecting = true;
    
    // Stop inactivity monitoring FIRST to prevent any timers from firing
    this.stopInactivityMonitoring();
    
    // Clear all inactivity timers aggressively
    this.clearInactivityTimers();
    
    // Stop heartbeat
    this.stopHeartbeat();
    
    // Stop audio capture first and clear the instance completely
    if (this.audioCapture) {
      logger.log('[VoiceModeService] 🎤 Stopping audio capture...');
      try {
        await this.audioCapture.stop();
        logger.log('[VoiceModeService] ✅ Audio capture stopped successfully');
      } catch (error) {
        logger.error('[VoiceModeService] ❌ Error stopping audio capture:', error);
      }
      this.audioCapture = null;
      logger.log('[VoiceModeService] Audio capture reference cleared');
    } else {
      logger.log('[VoiceModeService] No audio capture instance to stop');
    }
    
    // Stop audio playback
    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
    }
    
    // Clear audio queue
    this.audioQueue = [];
    this.activeSourceNodes = []; // Clear any stale source nodes from previous session
    this.isPlayingAudio = false;
    this.nextPlaybackTime = null;
    this.currentTranscript = '';
    this.currentUserTranscript = '';
    this.pendingAssistantCompletion = false;
    this.pendingUserTranscriptCompletion = false;
    this.isUserSpeaking = false;
    this.currentAssistantTurnId = null;
    this.currentUserTurnId = null;
    this.hasFunctionCallInProgress = false;
    this.hasFunctionCallBeenReceived = false;
    this.audioCompleteReceived = false;
    this.isPaused = false; // Reset paused state
    
    if (this.socket) {
      logger.log('[VoiceModeService] Stopping session and disconnecting');
      
      // Remove all listeners before disconnecting
      this.socket.removeAllListeners();
      
      // Notify server we're stopping the session
      this.socket.emit('stop_session');
      this.socket.disconnect();
      this.socket = null;
    }
    
    // Notify callbacks AFTER socket is cleared
    this.callbacks.onStatusChange?.('disconnected');
    
    // Clear callbacks to prevent stale closures from affecting next session
    this.callbacks = {};
    
    // Reset event tracking
    this.eventTracking = {
      textDeltaUserCount: 0,
      textDeltaAssistantCount: 0,
      speechStartedCount: 0,
      speechStoppedCount: 0,
      transcriptionCompleteCount: 0,
      userTranscriptCount: 0,
      userTextDeltaExamples: []
    };
    
    // Reset disconnecting flag so we can connect again
    this.isDisconnecting = false;
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Check if audio capture is active (microphone is listening)
   */
  isListening(): boolean {
    return this.audioCapture !== null && !this.isPaused;
  }

  /**
   * Get the socket instance (for advanced usage)
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Start heartbeat/keepalive signals (only when assistant is active)
   */
  private startHeartbeat(): void {
    // Don't start if already running
    if (this.heartbeatInterval) {
      return;
    }
    
    this.isAssistantActive = true;
    
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.connected) {
        this.socket.emit('keepalive', { timestamp: Date.now() });
        logger.log('[VoiceModeService] 💓 Keepalive sent');
      }
    }, 20000); // Send every 20 seconds
    
    logger.log('[VoiceModeService] 💓 Heartbeat started');
  }

  /**
   * Stop heartbeat/keepalive signals
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
      this.isAssistantActive = false;
      logger.log('[VoiceModeService] 💓 Heartbeat stopped');
    }
  }

  /**
   * Test WebSocket connectivity independently (for debugging)
   * This creates a raw WebSocket connection to diagnose issues
   */
  async testConnection(serverUrl: string = 'ws://localhost:8000'): Promise<void> {
    logger.log('[VoiceModeService] Testing raw WebSocket connection...');
    
    try {
      const accounts = this.msalInstance?.getAllAccounts();
      const userEmail = accounts?.[0]?.username || 'unknown@user.com';
      const upin = userEmail;  // Use full email
      
      // Construct the full WebSocket URL that Socket.IO will use
      const wsUrl = `${serverUrl.replace('http', 'ws')}/voice-live/?upin=${encodeURIComponent(upin)}&EIO=4&transport=websocket`;
      
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = (event) => {
        logger.log('[VoiceModeService] Successful connection - Connection details:', {
          url: ws.url,
          protocol: ws.protocol,
          extensions: ws.extensions,
          readyState: ws.readyState,
          bufferedAmount: ws.bufferedAmount
        });
        ws.close();
      };
      
      ws.onerror = (event) => {
        logger.error('[VoiceModeService] Connection FAILED - error event:', {
          type: event.type,
          target: event.target,
          isTrusted: event.isTrusted
        });
      };
      
      ws.onclose = (event) => {
        logger.log('[VoiceModeService] WebSocket closed:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          closeCodeMeaning: this.getCloseCodeMeaning(event.code)
        });
      };
      
      // Timeout after 10 seconds
      setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          logger.error('[VoiceModeService] Connection test timed out after 10s');
          ws.close();
        }
      }, 10000);
      
    } catch (error) {
      logger.error('[VoiceModeService] Test connection error:', error);
    }
  }

  /**
   * Get human-readable meaning for WebSocket close codes
   */
  private getCloseCodeMeaning(code: number): string {
    const closeCodes: { [key: number]: string } = {
      1000: 'Normal Closure',
      1001: 'Going Away',
      1002: 'Protocol Error',
      1003: 'Unsupported Data',
      1004: 'Reserved',
      1005: 'No Status Received',
      1006: 'Abnormal Closure (no close frame)',
      1007: 'Invalid Frame Payload Data',
      1008: 'Policy Violation',
      1009: 'Message Too Big',
      1010: 'Mandatory Extension',
      1011: 'Internal Server Error',
      1012: 'Service Restart',
      1013: 'Try Again Later',
      1014: 'Bad Gateway',
      1015: 'TLS Handshake Failure'
    };
    return closeCodes[code] || `Unknown code (${code})`;
  }

  /**
   * Pause audio capture (stop sending audio to server)
   * Keeps the connection alive but stops microphone input
   */
  async pauseAudioCapture(): Promise<void> {
    if (!this.audioCapture) {
      logger.warn('[VoiceModeService] Cannot pause - no audio capture active');
      return;
    }

    logger.log('[VoiceModeService] 🎤 Pausing audio capture');
    this.isPaused = true;
    
    // Stop the audio capture completely
    await this.audioCapture.stop();
    this.audioCapture = null;
  }

  /**
   * Resume audio capture (restart sending audio to server)
   * Requires re-initializing the microphone
   */
  async resumeAudioCapture(): Promise<void> {
    // Check if there's already an active audio capture and stop it
    const existingCapture = this.audioCapture;
    if (existingCapture) {
      logger.log('[VoiceModeService] ⚠️ Found existing audio capture during resume - stopping it first');
      try {
        await existingCapture.stop();
      } catch (err) {
        logger.error('[VoiceModeService] Error stopping existing audio capture during resume:', err);
      }
      this.audioCapture = null;
    }

    if (!this.socket?.connected) {
      logger.error('[VoiceModeService] Cannot resume - not connected to server');
      throw new Error('Not connected to server');
    }

    logger.log('[VoiceModeService] 🎤 Resuming audio capture');
    this.isPaused = false;

    // On resume we intentionally leave the countdown interval running so the tab
    // title keeps ticking down. We only clear the isInactivityWarningActive flag
    // so the service won't fire another forced-pause while the user is speaking.
    // The countdown interval itself will be cleared naturally when:
    //   a) the backend confirms speech via speech_started → clearInactivityTimers(), or
    //   b) the 60-second hard limit fires → triggerInactivityTimeout().
    if (this.isInactivityWarningActive) {
      logger.log('[VoiceModeService] Resuming with active inactivity warning - keeping countdown running, clearing forced-pause guard only');
      this.isInactivityWarningActive = false;
      // Do NOT call clearInactivityTimers() here — the interval must keep ticking.
    }

    // Small delay to ensure previous AudioContext is fully closed AND backend is ready
    // This matches the delay used in initial connection and prevents race conditions
    logger.log('[VoiceModeService] Waiting 200ms for AudioContext cleanup and backend readiness...');
    await new Promise(resolve => setTimeout(resolve, 200));

    // Re-initialize audio capture with same configuration
    try {
      const audioCaptureManager = new AudioCaptureManager();
      this.audioCapture = audioCaptureManager;
      
      await audioCaptureManager.start({
        deviceId: this.callbacks.deviceId,
        onAudioData: (base64Audio) => {
          if (this.socket?.connected && !this.isPaused) {
            this.socket.emit('audio_stream', { audio: base64Audio });
          } else if (this.isPaused && Math.random() < 0.01) {
            // Only log occasionally when paused to avoid spam
            logger.log('[VoiceModeService] Audio capture paused, not sending audio');
          } else if (!this.socket?.connected && Math.random() < 0.01) {
            logger.warn('[VoiceModeService] Socket not connected, cannot send audio');
          }
        },
        onAudioLevel: (level) => {
          if (!this.hasEverDetectedAudio && level > 0.05) {
            this.hasEverDetectedAudio = true;
            logger.log('[VoiceModeService] 🎙️ Audio level detected on frontend during resume (level:', level.toFixed(3), ') - suppressing no-audio warning');
          }
          this.callbacks.onAudioLevel?.(level);
        },
        onFrequencyData: (data) => {
          this.callbacks.onFrequencyData?.(data);
        },
        onError: (error) => {
          logger.error('[VoiceModeService] Audio capture error:', error);
          this.callbacks.onError?.(error);
        },
        onNoAudioDetected: () => {
          logger.warn('[VoiceModeService] No audio detected from microphone');
          this.callbacks.onNoAudioDetected?.();
        }
      });
      
      logger.log('[VoiceModeService] ✅ Audio capture resumed successfully');
    } catch (error) {
      logger.error('[VoiceModeService] Failed to resume audio capture:', error);
      this.isPaused = true; // Keep paused state if resume fails
      throw error;
    }
  }

  /**
   * Check if audio capture is currently paused
   */
  isPausedState(): boolean {
    return this.isPaused;
  }

  /**
   * Start inactivity timer only if system is completely idle
   * (no audio playing, no function calls in progress)
   */
  private startInactivityTimerIfIdle(): void {
    // Don't start timer if:
    // - Function call is in progress
    // - Audio is still playing
    if (this.hasFunctionCallInProgress || this.activeSourceNodes.length > 0) {
      logger.log('[VoiceModeService] ⏸️ Not starting inactivity timer - activity in progress:', {
        hasFunctionCallInProgress: this.hasFunctionCallInProgress,
        activeSourcesCount: this.activeSourceNodes.length
      });
      return;
    }
    
    // Clear any existing timers first
    this.clearInactivityTimers();
    
    // Update last activity timestamp
    this.lastActivityTimestamp = Date.now();
    
    // Start new 30-second warning timer
    logger.log('[VoiceModeService] 🕐 Starting 30-second inactivity timer (system is idle)');
    this.inactivityWarningTimer = setTimeout(() => {
      this.triggerInactivityWarning();
    }, 30000); // 30 seconds
  }

  /**
   * Trigger inactivity warning (30 seconds of no speech)
   */
  /**
   * Re-start the inactivity countdown interval after a silent reconnect,
   * picking up from wherever the timer was when the socket dropped.
   * Called by ChatInput after startVoiceMode() completes during an unpause-reconnect.
   */
  public resumeCountdown(secondsRemaining: number): void {
    // Clear anything connect() may have left behind
    this.clearInactivityTimers();
    if (secondsRemaining <= 0) return;

    logger.log('[VoiceModeService] ▶️ Resuming countdown from', secondsRemaining, 'seconds');
    // Mirror the flag so the interval guard knows we're in a warning state
    this.isInactivityWarningActive = true;

    // Dispatch current value immediately so UI reflects it right away
    this.callbacks.onInactivityCountdown?.(secondsRemaining);

    let secs = secondsRemaining;
    this.inactivityCountdownInterval = setInterval(() => {
      if (!this.inactivityCountdownInterval) return;
      secs--;
      this.callbacks.onInactivityCountdown?.(secs);
      if (secs <= 0) {
        this.clearInactivityTimers();
        this.triggerInactivityTimeout();
      }
    }, 1000);

    // Safety-net hard timeout for however many seconds remain
    this.inactivityCountdownTimer = setTimeout(() => {
      if (this.isInactivityWarningActive) {
        this.triggerInactivityTimeout();
      }
    }, secondsRemaining * 1000);
  }

  private async triggerInactivityWarning(): Promise<void> {
    // Don't trigger if function call in progress or audio still playing
    if (this.hasFunctionCallInProgress || this.activeSourceNodes.length > 0) {
      logger.log('[VoiceModeService] ⏸️ Skipping inactivity warning - function call in progress:', this.hasFunctionCallInProgress, 'or audio playing:', this.activeSourceNodes.length > 0);
      return;
    }
    
    logger.log('[VoiceModeService] ⚠️ Inactivity warning triggered - 30 seconds of no speech');
    this.isInactivityWarningActive = true;
    
    // Actually pause the audio capture (stop microphone)
    await this.pauseAudioCapture();
    // Note: pauseAudioCapture sets isPaused = true internally
    this.callbacks.onInactivityWarning?.();
    
    // Notify UI of pause state change
    if (this.callbacks.onPauseStateChange) {
      logger.log('[VoiceModeService] 📞 Calling onPauseStateChange(true)');
      this.callbacks.onPauseStateChange(true);
    }
    
    // Start 60-second countdown
    let secondsRemaining = 60;
    logger.log('[VoiceModeService] 📞 Calling onInactivityCountdown callback with:', secondsRemaining, 'exists:', !!this.callbacks.onInactivityCountdown);
    this.callbacks.onInactivityCountdown?.(secondsRemaining);
    
    // Update countdown every second
    this.inactivityCountdownInterval = setInterval(() => {
      // Guard: if the interval itself was cleared (null), the closure is stale — bail out.
      // We do NOT check isInactivityWarningActive here because the user may have unpaused
      // (which clears that flag) but we still need the countdown to keep ticking until
      // the backend confirms actual speech via speech_started.
      if (!this.inactivityCountdownInterval) {
        return;
      }
      secondsRemaining--;
      logger.log('[VoiceModeService] ⏱️ Countdown tick:', secondsRemaining);
      this.callbacks.onInactivityCountdown?.(secondsRemaining);
      
      if (secondsRemaining <= 0) {
        this.clearInactivityTimers();
        this.triggerInactivityTimeout();
      }
    }, 1000);
    
    // Trigger timeout after 60 seconds as a safety net
    // (the interval above is the primary driver; this catches edge cases)
    this.inactivityCountdownTimer = setTimeout(() => {
      if (this.isInactivityWarningActive) {
        this.triggerInactivityTimeout();
      }
    }, 60000); // 60 seconds
  }

  /**
   * Trigger inactivity timeout (voice mode should end)
   */
  private triggerInactivityTimeout(): void {
    // Guard: don't fire if the session already ended or the warning was cleared
    // (e.g. user manually stopped voice mode or spoke before timeout)
    if (!this.isInactivityWarningActive && !this.socket?.connected) {
      logger.log('[VoiceModeService] ⏰ Inactivity timeout suppressed - session already ended');
      return;
    }
    logger.log('[VoiceModeService] ⏰ Inactivity timeout - ending voice mode');
    this.clearInactivityTimers();
    this.callbacks.onInactivityTimeout?.();
  }

  /**
   * Clear all inactivity timers
   */
  private clearInactivityTimers(): void {
    logger.log('[VoiceModeService] 🕐 Clearing all inactivity timers');
    if (this.inactivityWarningTimer) {
      clearTimeout(this.inactivityWarningTimer);
      this.inactivityWarningTimer = null;
      logger.log('[VoiceModeService] ✅ Cleared warning timer');
    }
    if (this.inactivityCountdownTimer) {
      clearTimeout(this.inactivityCountdownTimer);
      this.inactivityCountdownTimer = null;
      logger.log('[VoiceModeService] ✅ Cleared countdown timer');
    }
    if (this.inactivityCountdownInterval) {
      clearInterval(this.inactivityCountdownInterval);
      this.inactivityCountdownInterval = null;
      logger.log('[VoiceModeService] ✅ Cleared countdown interval');
    }
    this.isInactivityWarningActive = false;
  }

  /**
   * Start inactivity monitoring (call after assistant is ready)
   */
  startInactivityMonitoring(): void {
    logger.log('[VoiceModeService] 🕐 Starting inactivity monitoring');
    this.startInactivityTimerIfIdle();
  }

  /**
   * Stop inactivity monitoring (call when disconnecting)
   */
  stopInactivityMonitoring(): void {
    logger.log('[VoiceModeService] 🕐 Stopping inactivity monitoring');
    this.clearInactivityTimers();
  }
}

// Export a singleton instance
export const voiceModeService = new VoiceModeService();
